// src/components/exam/charts/StrengthsWeaknesses.jsx
import React from 'react';
import { Trophy, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';

const StrengthsWeaknesses = ({ strengths, weaknesses }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Strengths */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-green-100 p-3 rounded-lg">
            <Trophy className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Your Strengths
            </h3>
            <p className="text-sm text-gray-600">
              Keep up the great work!
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {strengths.length > 0 ? (
            strengths.map((strength, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 border border-green-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">
                    {strength.name}
                  </span>
                  <span className="text-lg font-bold text-green-600">
                    {strength.score}%
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-green-100 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${strength.score}%` }}
                  />
                </div>

                {/* Trend indicator */}
                {strength.trend === 'improving' && (
                  <div className="flex items-center gap-1 mt-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">
                      Improving trend
                    </span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-600 text-center py-4">
              Complete more exams to identify your strengths
            </p>
          )}
        </div>
      </div>

      {/* Weaknesses */}
      <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-orange-100 p-3 rounded-lg">
            <AlertCircle className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Areas to Improve
            </h3>
            <p className="text-sm text-gray-600">
              Focus your practice here
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {weaknesses.length > 0 ? (
            weaknesses.map((weakness, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 border border-orange-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">
                    {weakness.name}
                  </span>
                  <span className="text-lg font-bold text-orange-600">
                    {weakness.score}%
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-orange-100 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${weakness.score}%` }}
                  />
                </div>

                {/* Trend indicator */}
                {weakness.trend === 'declining' && (
                  <div className="flex items-center gap-1 mt-2">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    <span className="text-xs text-red-600 font-medium">
                      Needs attention
                    </span>
                  </div>
                )}

                {/* Action suggestion */}
                <div className="mt-3 pt-3 border-t border-orange-100">
                  <button className="text-xs text-orange-600 hover:text-orange-700 font-medium transition-colors">
                    Practice {weakness.name} →
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-600 text-center py-4">
              Complete more exams to identify areas for improvement
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrengthsWeaknesses;